import dayjs from "dayjs"
import { Checkbox, DatePicker, Input, Select, Tooltip } from "antd"
import { isEmpty, trim } from "lodash"
import { useEffect, useMemo, useState } from "react"
import { Button as BootstrapButton, Col, Form, Row } from "react-bootstrap"
import CheckGroup from "../Common/components/CheckGroup"
import { Tips } from "../Common/Tooltip"
import * as com from "../Common"
import * as st from "../State"
import "./styles.css"

const FieldError = ({ message }) => (
    message ? <div className="warning-msg">{message}</div> : null
)

const FIELD_ORDER = [
    "firstName",
    "lastName",
    "email",
    "dateOfBirth",
    "citizenship",
    "visaExpiration",
    "maritalStatus",
    "phone",
]

const scrollToField = (fieldKey) => {
    if (!fieldKey || typeof document === "undefined") return
    const el = document.querySelector(`[data-field="${fieldKey}"]`)
    if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

const suffixOptions = ["Jr.", "Sr.", "I", "II", "III", "IV", "Ph.D.", "M.D.", "Esq.", "J.D.", "CPA", "DDS", "KBE", "OBE", "MBE"]
const visaOptions = [
    st.VT_A_1,
    st.VT_E_1,
    st.VT_E_2,
    st.VT_E_3,
    st.VT_F_1,
    st.VT_G_1,
    st.VT_G_2,
    st.VT_G_3,
    st.VT_G_4,
    st.VT_H_1B,
    st.VT_H_4,
    st.VT_L_1,
    st.VT_L_2,
    st.VT_O_1,
    st.VT_O_2,
    st.VT_O_3,
    st.VT_P_1,
    st.VT_P_2,
    st.VT_P_3,
    st.VT_P_4,
    st.VT_TN,
]
const phoneTypes = ["home", "mobile", "work"]
const borrowerExcludedVisaOptions = [st.VT_H_4, st.VT_L_2, st.VT_O_3, st.VT_P_4]

const emptyProfile = {
    firstName: "",
    middleName: "",
    lastName: "",
    suffix: "",
    email: "",
    dateOfBirth: "",
    maritalStatus: "",
    hasUnmarriedRelationship: "",
    unmarriedRelationship: "",
    otherUnmarriedRelationship: "",
    stateOfUnmarriedRelationship: "",
    citizenship: "",
    visa: "",
    visaExpiration: "",
    homePhone: "",
    mobilePhone: "",
    workPhone: "",
    numberOfDependents: "",
    dependentsAges: "",
}

const MAX_DEPENDENTS = 20

const parseDependentCount = (value) => {
    if (value === "" || value == null) {
        return 0
    }
    const n = parseInt(value, 10)
    if (Number.isNaN(n) || n < 0) {
        return 0
    }
    return Math.min(MAX_DEPENDENTS, n)
}

const splitDependentsAges = (value, count) => (
    Array.from({ length: count }, (_, i) => {
        const parts = `${value || ""}`.split(",")
        return (parts[i] || "").trim()
    })
)

const joinDependentsAges = (ages) => ages.map((age) => `${age || ""}`.trim()).join(",")

const emptyApplicationSettings = {
    hascoborrower: "",
    cohabiting: null,
    cohabitingPartyId: "",
    married: null,
    assetsofotherpersonused: false,
    liabilitiesofotherpersonrequired: false,
    leadSource: "",
}

const firstValue = (...values) => values.find((value) => value != null && value !== "") || ""

const toProfile = (participant) => ({
    ...emptyProfile,
    firstName: firstValue(participant?.firstName, participant?.firstname),
    middleName: firstValue(participant?.middleName, participant?.middlename),
    lastName: firstValue(participant?.lastName, participant?.lastname),
    suffix: participant?.suffix || "",
    email: participant?.email || "",
    dateOfBirth: firstValue(participant?.dateOfBirth, participant?.dateofbirth),
    maritalStatus: firstValue(participant?.maritalStatus, participant?.maritalstatus),
    hasUnmarriedRelationship: firstValue(participant?.hasUnmarriedRelationship, participant?.hasunmarriedrelationship),
    unmarriedRelationship: firstValue(participant?.unmarriedRelationship, participant?.unmarriedrelationship),
    otherUnmarriedRelationship: firstValue(participant?.otherUnmarriedRelationship, participant?.otherunmarriedrelationship),
    stateOfUnmarriedRelationship: firstValue(participant?.stateOfUnmarriedRelationship, participant?.stateofunmarriedrelationship),
    citizenship: participant?.citizenship || "",
    visa: participant?.visa || "",
    visaExpiration: firstValue(participant?.visaExpiration, participant?.visaexpiration),
    homePhone: firstValue(participant?.homePhone, participant?.homephone),
    mobilePhone: firstValue(participant?.mobilePhone, participant?.mobilephone),
    workPhone: firstValue(participant?.workPhone, participant?.workphone),
    numberOfDependents: firstValue(participant?.numberOfDependents, participant?.numberofdependents),
    dependentsAges: firstValue(participant?.dependentsAges, participant?.dependentsages),
})

const dateValue = (value) => (value ? dayjs(value, "MM/DD/YYYY") : null)

const phoneFieldName = (type) => `${type}Phone`
const isPhoneEmpty = (value) => !value
const isRealPhone = (value) => value && value !== com.phonePlaceholder

const PhoneInputV2 = ({ type, value, onDelete, onChange, availableTypes, onTypeChange, showDelete = true, error }) => {
    const [displayValue, setDisplayValue] = useState(value === com.phonePlaceholder ? "" : value)

    useEffect(() => {
        setDisplayValue(value === com.phonePlaceholder ? "" : value)
    }, [value])

    const handleChange = (event) => {
        const newValue = event.target.value.slice(0, 14)
        setDisplayValue(newValue)
        onChange({
            target: {
                value: newValue || com.phonePlaceholder,
            },
        })
    }

    return (
        <div style={{ flex: "1 1 0" }}>
            <div md={4} xs={12}>
                <div className="mb-3">
                    {showDelete && (
                        <div className="d-flex justify-content-end mb-1">
                            <span
                                style={{ cursor: "pointer", color: "#C21818", fontWeight: 500, fontSize: "16px" }}
                                onClick={() => onDelete(type)}
                            >
                                Delete
                            </span>
                        </div>
                    )}
                    <div className="phone-input-container">
                        <Form.Control
                            className="phone-input-form-control"
                            isValid={false}
                            isInvalid={!!error}
                            placeholder={com.phonePlaceholder}
                            value={displayValue}
                            onChange={handleChange}
                            required
                            type="tel"
                            pattern={com.phoneRegex}
                            inputMode="numeric"
                            onKeyPress={(event) => {
                                if (!/[0-9-()\s]/.test(event.key)) {
                                    event.preventDefault()
                                }
                            }}
                        />
                        <div className="phone-input-separator" style={{ width: "1px", background: "#d9d9d9" }} />
                        <Select
                            style={{ flex: "1", height: "44px", width: "100%", background: "#FCFCFC", overflow: "hidden", minWidth: 95 }}
                            className="contact-select"
                            placeholder="Type"
                            value={type}
                            onChange={onTypeChange}
                        >
                            {availableTypes.map((phoneType) => (
                                <Select.Option key={phoneType} value={phoneType}>
                                    {phoneType.charAt(0).toUpperCase() + phoneType.slice(1)}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <FieldError message={error} />
                </div>
            </div>
        </div>
    )
}

const toApplicationSettings = (application) => ({
    ...emptyApplicationSettings,
    hascoborrower: application?.hascoborrower || "",
    cohabiting: application?.cohabiting ?? null,
    cohabitingPartyId: application?.cohabitingPartyId || "",
    married: application?.married ?? null,
    assetsofotherpersonused: !!application?.assetsofotherpersonused,
    liabilitiesofotherpersonrequired: !!application?.liabilitiesofotherpersonrequired,
    leadSource: application?.leadSource || "",
})

const coborrowerEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const participantCount = (application) => application?.participantOrder?.length || 0

const participantDisplayName = (participant) => {
    if (!participant) {
        return "Another co-borrower"
    }
    const name = [participant.firstName, participant.middleName, participant.lastName]
        .filter((item) => !isEmpty(trim(item || "")))
        .join(" ")
    return name || (participant.isPrimary ? "Borrower" : "Co-borrower")
}

const YourInfoV2 = ({ application, participant, saving, onSave }) => {
    const [profile, setProfile] = useState(emptyProfile)
    const [applicationSettings, setApplicationSettings] = useState(emptyApplicationSettings)
    const [fieldErrors, setFieldErrors] = useState({})

    useEffect(() => {
        setProfile(toProfile(participant))
        setFieldErrors({})
    }, [participant])

    useEffect(() => {
        setApplicationSettings(toApplicationSettings(application))
    }, [application])

    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    const isAlien = profile.citizenship === "alien"
    const isUnmarried = profile.maritalStatus === "unmarried"
    const isCoborrower = !participant?.isPrimary
    const showLeadSource = !isCoborrower && com.getCustomerId() === "ratedna"
    const hasAnyCoborrower = participantCount(application) > 1
    const marriedToPrimaryPartyId = applicationSettings.cohabitingPartyId || ""
    const isMarriedToPrimary = !!participant?.partyId && marriedToPrimaryPartyId === participant.partyId
    const otherMarriedSpouse = useMemo(() => {
        if (!marriedToPrimaryPartyId || isMarriedToPrimary) {
            return null
        }
        return application?.participantsById?.[marriedToPrimaryPartyId] || null
    }, [application?.participantsById, isMarriedToPrimary, marriedToPrimaryPartyId])
    const marriedToPrimaryLocked = !!otherMarriedSpouse || (!!marriedToPrimaryPartyId && !isMarriedToPrimary)
    const marriedToPrimaryTooltip = marriedToPrimaryLocked
        ? `${participantDisplayName(otherMarriedSpouse)} is already married to the primary applicant.`
        : ""
    const visibleVisaOptions = isCoborrower
        ? visaOptions
        : visaOptions.filter((visa) => !borrowerExcludedVisaOptions.includes(visa))

    const setMarriedToPrimary = (checked) => {
        if (marriedToPrimaryLocked) {
            return
        }
        // Legacy onMarried: toggle cohabiting and application.married together.
        setApplicationSettings((prev) => ({
            ...prev,
            cohabiting: checked,
            cohabitingPartyId: checked ? participant?.partyId || "" : "",
            married: checked,
        }))
    }

    const setField = (field) => (eventOrValue) => {
        const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue
        setProfile((prev) => ({ ...prev, [field]: value || "" }))
        clearFieldError(field)
        if (field === "citizenship" && value !== "alien") {
            clearFieldError("visaExpiration")
        }
        if (
            field === "maritalStatus"
            && value !== "married"
            && applicationSettings.cohabitingPartyId === participant?.partyId
        ) {
            setApplicationSettings((prev) => ({
                ...prev,
                cohabiting: false,
                cohabitingPartyId: "",
                married: false,
            }))
        }
    }

    const dependentCount = parseDependentCount(profile.numberOfDependents)
    const dependentAges = splitDependentsAges(profile.dependentsAges, dependentCount)

    const handleDependentsCountChange = (value) => {
        const raw = `${value ?? ""}`.replace(/\D/g, "")
        if (raw === "") {
            setProfile((prev) => ({
                ...prev,
                numberOfDependents: "",
                dependentsAges: "",
            }))
            return
        }
        const n = parseDependentCount(raw)
        setProfile((prev) => ({
            ...prev,
            numberOfDependents: String(n),
            dependentsAges: n > 0
                ? joinDependentsAges(splitDependentsAges(prev.dependentsAges, n))
                : "",
        }))
    }

    const handleDependentAgeChange = (index) => (value) => {
        const nextValue = `${value ?? ""}`.replace(/\D/g, "")
        setProfile((prev) => {
            const count = parseDependentCount(prev.numberOfDependents)
            const ages = splitDependentsAges(prev.dependentsAges, count)
            ages[index] = nextValue
            return {
                ...prev,
                dependentsAges: joinDependentsAges(ages),
            }
        })
    }

    const setPhoneField = (field) => (event) => {
        setProfile((prev) => ({ ...prev, [field]: com.fixPhoneInput(event.target.value) }))
        setFieldErrors((prev) => {
            if (!prev.phone && !prev[field]) return prev
            const next = { ...prev }
            delete next.phone
            delete next[field]
            return next
        })
    }

    const getUsedPhoneTypes = () => phoneTypes.filter((type) => !isPhoneEmpty(profile[phoneFieldName(type)]))
    const visiblePhoneCount = getUsedPhoneTypes().length

    const handleDeletePhone = (phoneType) => {
        if (getUsedPhoneTypes().length > 1) {
            setField(phoneFieldName(phoneType))("")
        }
    }

    const handleChangePhoneType = (oldType, newType, currentValue) => {
        if (oldType === newType) {
            return
        }
        const usedTypes = getUsedPhoneTypes()
        if (usedTypes.includes(newType)) {
            return
        }
        const currentValueToUse = currentValue || com.phonePlaceholder
        setProfile((prev) => ({
            ...prev,
            [phoneFieldName(newType)]: currentValueToUse,
            [phoneFieldName(oldType)]: "",
        }))
    }

    const handleAddPhone = () => {
        const emptyType = phoneTypes.find((type) => isPhoneEmpty(profile[phoneFieldName(type)]))
        if (emptyType) {
            setField(phoneFieldName(emptyType))(com.phonePlaceholder)
        }
    }

    const renderPhoneInput = (type) => {
        const value = profile[phoneFieldName(type)]
        const shouldShow = type === "home"
            ? !isPhoneEmpty(value) || getUsedPhoneTypes().length === 0
            : !isPhoneEmpty(value)

        if (!shouldShow) {
            return null
        }

        const phoneKey = phoneFieldName(type)
        const phoneError = fieldErrors[phoneKey]

        return (
            <Col key={type} md={4} className={visiblePhoneCount > 1 ? "yourinfo-v2-phone-col" : undefined}>
                <PhoneInputV2
                    type={type}
                    value={value}
                    onDelete={handleDeletePhone}
                    onChange={setPhoneField(phoneKey)}
                    availableTypes={[type, ...phoneTypes.filter((phoneType) => (
                        phoneType !== type && !getUsedPhoneTypes().includes(phoneType)
                    ))]}
                    onTypeChange={(newType) => handleChangePhoneType(type, newType, value)}
                    showDelete={visiblePhoneCount > 1}
                    error={phoneError}
                />
            </Col>
        )
    }

    const normalizedProfile = useMemo(() => {
        let next = { ...profile }
        const dependentCount = parseDependentCount(next.numberOfDependents)

        if (next.numberOfDependents === "" || next.numberOfDependents == null) {
            next = {
                ...next,
                numberOfDependents: "",
                dependentsAges: "",
            }
        } else if (dependentCount === 0) {
            next = {
                ...next,
                numberOfDependents: "0",
                dependentsAges: "",
            }
        } else {
            next = {
                ...next,
                numberOfDependents: String(dependentCount),
                dependentsAges: joinDependentsAges(splitDependentsAges(next.dependentsAges, dependentCount)),
            }
        }

        if (next.citizenship !== "alien") {
            next = {
                ...next,
                visa: "",
                visaExpiration: "",
            }
        }

        if (next.maritalStatus !== "unmarried") {
            return {
                ...next,
                hasUnmarriedRelationship: "",
                unmarriedRelationship: "",
                otherUnmarriedRelationship: "",
                stateOfUnmarriedRelationship: "",
            }
        }

        if (next.hasUnmarriedRelationship !== "yes") {
            return {
                ...next,
                unmarriedRelationship: "",
                otherUnmarriedRelationship: "",
                stateOfUnmarriedRelationship: "",
            }
        }

        if (next.unmarriedRelationship !== "other") {
            return {
                ...next,
                otherUnmarriedRelationship: "",
            }
        }

        return next
    }, [profile])

    const normalizedApplicationSettings = useMemo(() => {
        let hascoborrower = hasAnyCoborrower
            ? (applicationSettings.hascoborrower || "withcoborrower")
            : "alone"

        let married = applicationSettings.married
        let cohabiting = applicationSettings.cohabiting
        let cohabitingPartyId = applicationSettings.cohabitingPartyId
        let assetsofotherpersonused = applicationSettings.assetsofotherpersonused
        let liabilitiesofotherpersonrequired = applicationSettings.liabilitiesofotherpersonrequired

        if (hascoborrower === "alone") {
            married = false
            cohabiting = false
            cohabitingPartyId = ""
            assetsofotherpersonused = false
            liabilitiesofotherpersonrequired = false
        }

        if (
            isCoborrower
            && profile.maritalStatus !== "married"
            && cohabitingPartyId === participant?.partyId
        ) {
            cohabiting = false
            cohabitingPartyId = ""
            married = false
        }

        // Loan-level spouse flags follow cohabitingPartyId (legacy married↔cohabiting toggle).
        if (cohabitingPartyId) {
            cohabiting = true
            married = true
        } else if (hascoborrower === "withcoborrower") {
            cohabiting = false
            if (married == null) {
                married = true
            }
        }

        return {
            hascoborrower,
            married,
            cohabiting,
            cohabitingPartyId,
            assetsofotherpersonused,
            liabilitiesofotherpersonrequired,
            ...(showLeadSource ? { leadSource: applicationSettings.leadSource || "" } : {}),
        }
    }, [applicationSettings, hasAnyCoborrower, isCoborrower, participant?.partyId, profile.maritalStatus, showLeadSource])

    const validateProfile = () => {
        const next = normalizedProfile
        const errors = {}

        if (!next.firstName) errors.firstName = "First name is required."
        if (!next.lastName) errors.lastName = "Last name is required."
        if (!next.dateOfBirth) errors.dateOfBirth = "Date of birth is required."
        if (!next.citizenship) errors.citizenship = "Citizenship is required."
        if (!next.maritalStatus) errors.maritalStatus = "Marital status is required."

        // Legacy coborrower email is optional; invalid values are cleared on blur.
        if (isCoborrower && next.email && !coborrowerEmailRegex.test(next.email)) {
            errors.email = "please enter your co-applicant's vaild email address"
        }

        // Legacy: visa Select is not HTML-required; expiration is required when alien.
        if (next.citizenship === "alien" && !next.visaExpiration) {
            errors.visaExpiration = "Visa should be valid for at least 90 days."
        }

        const phones = phoneTypes.map((type) => next[phoneFieldName(type)]).filter(isRealPhone)
        if (phones.length === 0) {
            errors.phone = "At least one phone number is required."
        } else {
            phoneTypes.forEach((type) => {
                const value = next[phoneFieldName(type)]
                if (isRealPhone(value) && value.length !== 14) {
                    errors[phoneFieldName(type)] = "Incorrect phone number format."
                }
            })
        }

        const firstErrorKey = FIELD_ORDER.find((key) => {
            if (key === "phone") {
                return errors.phone || phoneTypes.some((type) => errors[phoneFieldName(type)])
            }
            return !!errors[key]
        }) || null

        return { errors, firstErrorKey }
    }

    const handleCoborrowerEmailBlur = () => {
        if (!isCoborrower) {
            return
        }
        const email = profile.email || ""
        if (!email) {
            clearFieldError("email")
            return
        }
        if (coborrowerEmailRegex.test(email)) {
            clearFieldError("email")
            return
        }
        setProfile((prev) => ({ ...prev, email: "" }))
        setFieldErrors((prev) => ({
            ...prev,
            email: "please enter your co-applicant's vaild email address",
        }))
    }

    const handleNext = () => {
        const { errors, firstErrorKey } = validateProfile()
        if (firstErrorKey) {
            setFieldErrors(errors)
            // Wait for error nodes to paint before scrolling
            setTimeout(() => scrollToField(firstErrorKey), 50)
            return
        }
        setFieldErrors({})

        // Primary has no email field; never overwrite existing email with "".
        const emailToSave = isCoborrower
            ? normalizedProfile.email
            : (normalizedProfile.email || participant?.email || "")

        onSave({
            ...normalizedProfile,
            email: emailToSave,
            application: normalizedApplicationSettings,
        })
    }

    return (
        <>
            <Row className="page-header-row">
                <Col>
                    <div className="page-header">
                        <span className="page-header-primary">Personal</span>
                        <span className="page-header-separator">/</span>
                        <span className="page-header-secondary">Your info</span>
                    </div>
                </Col>
            </Row>

            <div className="application-v2-form-body yourinfo-v2">
                <Row className="yourinfo-v2-row">
                    <Col md={4} xs={12}>
                        <Form.Group data-field="firstName">
                            <Form.Label>*First name</Form.Label>
                            <Form.Control
                                name="firstname"
                                placeholder="Enter your first name"
                                value={profile.firstName}
                                onChange={setField("firstName")}
                                isInvalid={!!fieldErrors.firstName}
                                required
                            />
                            <FieldError message={fieldErrors.firstName} />
                        </Form.Group>
                    </Col>
                    <Col md={4} xs={12}>
                        <Form.Group>
                            <Form.Label>Middle name</Form.Label>
                            <Form.Control
                                name="middlename"
                                placeholder="Enter your middle name"
                                value={profile.middleName}
                                onChange={setField("middleName")}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4} xs={12}>
                        <Form.Group data-field="lastName">
                            <Form.Label>*Last name</Form.Label>
                            <Form.Control
                                name="lastname"
                                placeholder="Enter your last name"
                                value={profile.lastName}
                                onChange={setField("lastName")}
                                isInvalid={!!fieldErrors.lastName}
                                required
                            />
                            <FieldError message={fieldErrors.lastName} />
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="yourinfo-v2-row">
                    <Col md={4} xs={12}>
                        <Form.Group className="text-left">
                            <Form.Label className="text-left">Suffix</Form.Label>
                            <Select
                                value={profile.suffix}
                                onChange={setField("suffix")}
                                style={{ width: "100%", height: "44px", background: "#FCFCFC" }}
                            >
                                <Select.Option value="">-- No Suffix --</Select.Option>
                                {suffixOptions.map((suffix) => (
                                    <Select.Option key={suffix} value={suffix}>{suffix}</Select.Option>
                                ))}
                            </Select>
                        </Form.Group>
                    </Col>
                    {isCoborrower && (
                        <Col md={4} xs={12}>
                            <Form.Group data-field="email">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    name="email"
                                    type="email"
                                    placeholder="Enter your co-applicant's email address"
                                    value={profile.email}
                                    onChange={setField("email")}
                                    onBlur={handleCoborrowerEmailBlur}
                                    isInvalid={!!fieldErrors.email}
                                />
                                <FieldError message={fieldErrors.email} />
                            </Form.Group>
                        </Col>
                    )}
                </Row>

                <Row className="yourinfo-v2-row">
                    <Col md={6} xs={12}>
                        <Form.Group className="text-left d-flex flex-column" data-field="dateOfBirth">
                            <Form.Label className="text-left text-nowrap">*Date of birth</Form.Label>
                            <DatePicker
                                name="dateofbirth"
                                style={{ width: "100%" }}
                                placeholder="MM/DD/YYYY"
                                format="MM/DD/YYYY"
                                status={fieldErrors.dateOfBirth ? "error" : undefined}
                                value={dateValue(profile.dateOfBirth)}
                                onChange={(value) => setField("dateOfBirth")(value ? dayjs(value).format("MM/DD/YYYY") : "")}
                            />
                            <FieldError message={fieldErrors.dateOfBirth} />
                        </Form.Group>
                    </Col>
                    <Col md={6} xs={12}>
                        <Form.Group controlId="citizenship" data-field="citizenship">
                            <Form.Label className="text-left">*What's your citizenship?</Form.Label>
                            <Select
                                value={profile.citizenship}
                                onChange={setField("citizenship")}
                                status={fieldErrors.citizenship ? "error" : undefined}
                                style={{ width: "100%", height: "44px", background: "#FCFCFC" }}
                            >
                                <Select.Option value="citizen">US Citizen</Select.Option>
                                <Select.Option value="permanent">Permanent Resident Alien</Select.Option>
                                <Select.Option value="alien">Non-Permanent Resident Alien</Select.Option>
                            </Select>
                            <FieldError message={fieldErrors.citizenship} />
                        </Form.Group>
                    </Col>
                </Row>

                {isAlien && (
                    <Row className="yourinfo-v2-row">
                        <Col md={6} xs={12}>
                            <Form.Group controlId="visa">
                                <Form.Label className="text-left">Visa:</Form.Label>
                                <Select
                                    value={profile.visa}
                                    onChange={setField("visa")}
                                    style={{ width: "100%", height: "44px", background: "#FCFCFC" }}
                                >
                                    {visibleVisaOptions.map((visa) => (
                                        <Select.Option key={visa} value={visa}>{visa}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Group>
                        </Col>
                        <Col md={6} xs={12}>
                            <Form.Group controlId="expiration" className="text-left" data-field="visaExpiration">
                                <Form.Label className="text-left">*Expires:</Form.Label>
                                <DatePicker
                                    disabledDate={(current) => current && current.valueOf() < dayjs().startOf("day").valueOf()}
                                    style={{ width: "100%" }}
                                    placeholder="MM/DD/YYYY"
                                    format="MM/DD/YYYY"
                                    status={fieldErrors.visaExpiration ? "error" : undefined}
                                    value={dateValue(profile.visaExpiration)}
                                    onChange={(value) => setField("visaExpiration")(value ? dayjs(value).format("MM/DD/YYYY") : "")}
                                />
                                <FieldError message={fieldErrors.visaExpiration} />
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                <Row className="yourinfo-v2-row yourinfo-v2-marital">
                    <Col md={12} xs={12}>
                        <Form.Group className="text-left" data-field="maritalStatus">
                            <Form.Label className="text-left">
                                *Marital status
                                <Tips content={<div>If you are in a domestic partnership, single or divorced, select Unmarried</div>} />
                            </Form.Label>
                            <CheckGroup
                                data={[
                                    { value: "married", label: "Married" },
                                    { value: "separated", label: "Separated" },
                                    { value: "unmarried", label: "Unmarried" },
                                ]}
                                value={profile.maritalStatus}
                                onChangeSelect={setField("maritalStatus")}
                                type="radio"
                                direction="horizontal"
                                style={{ display: "flex", width: "100%" }}
                            />
                            <FieldError message={fieldErrors.maritalStatus} />
                        </Form.Group>
                    </Col>
                </Row>

                {isCoborrower && profile.maritalStatus === "married" && (
                    <Row className="yourinfo-v2-row yourinfo-v2-married-primary">
                        <Col className="d-flex">
                            <Tooltip title={marriedToPrimaryTooltip}>
                                <span className={marriedToPrimaryLocked ? "yourinfo-v2-married-primary-disabled-wrap" : undefined}>
                                    <Checkbox
                                        checked={isMarriedToPrimary}
                                        disabled={marriedToPrimaryLocked}
                                        onChange={(event) => setMarriedToPrimary(event.target.checked)}
                                    >
                                        <span className="yourinfo-v2-married-primary-label">I am married to the primary applicant.</span>
                                    </Checkbox>
                                </span>
                            </Tooltip>
                        </Col>
                    </Row>
                )}

                {isUnmarried && (
                    <div className="yourinfo-v2-followup text-wrap">
                        <Form.Group className="inputSection">
                            <Form.Label>
                                Is there someone who isn't your legal spouse but has property rights similar to a spouse's, such as a registered domestic partner or a civil union partner?
                            </Form.Label>
                            <CheckGroup
                                className="w-600px w-100"
                                data={[
                                    { value: "yes", label: "Yes" },
                                    { value: "no", label: "No" },
                                ]}
                                onChangeSelect={setField("hasUnmarriedRelationship")}
                                value={profile.hasUnmarriedRelationship}
                                type="radio"
                                direction="horizontal"
                            />
                        </Form.Group>
                    </div>
                )}

                {isUnmarried && profile.hasUnmarriedRelationship === "yes" && (
                    <div className="yourinfo-v2-followup text-wrap">
                        <Form.Group className="unmarried-relationship-inputSection">
                            <Form.Label>
                                If you select yes, please indicate the type of relationship and the state in which it was formed
                            </Form.Label>
                            <CheckGroup
                                data={[
                                    { value: "civilunion", label: "Civil union" },
                                    { value: "domesticpartnership", label: "Domestic partnership" },
                                    { value: "reciprocalrelationship", label: "Registered reciprocal beneficiary relationship" },
                                    { value: "other", label: "Other" },
                                ]}
                                onChangeSelect={setField("unmarriedRelationship")}
                                value={profile.unmarriedRelationship}
                                type="radio"
                                direction="vertical"
                            />
                        </Form.Group>
                    </div>
                )}

                {isUnmarried && profile.unmarriedRelationship === "other" && (
                    <div className="yourinfo-v2-followup text-wrap">
                        <Form.Group>
                            <Form.Label>If you select other, please explain the type of relationship</Form.Label>
                            <Form.Control
                                name="otherunmarriedrelationship"
                                value={profile.otherUnmarriedRelationship}
                                onChange={setField("otherUnmarriedRelationship")}
                            />
                        </Form.Group>
                    </div>
                )}

                {isUnmarried && profile.hasUnmarriedRelationship === "yes" && (
                    <div className="yourinfo-v2-followup text-wrap">
                        <Form.Group>
                            <Form.Label>State (abbreviation) in which the relationship was formed</Form.Label>
                            <Form.Control
                                name="stateofunmarriedrelationship"
                                placeholder="eg. CA, NY, etc."
                                value={profile.stateOfUnmarriedRelationship}
                                onChange={setField("stateOfUnmarriedRelationship")}
                            />
                        </Form.Group>
                    </div>
                )}

                <Row className="yourinfo-v2-row yourinfo-v2-dependents">
                    <Col md={6} xs={12}>
                        <Form.Group>
                            <Form.Label>Dependents (not listed by another borrower)</Form.Label>
                            <Input
                                className="yourinfo-v2-prefix-field"
                                addonBefore="Number"
                                size="large"
                                value={profile.numberOfDependents}
                                onChange={(event) => handleDependentsCountChange(event.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {dependentCount > 0 ? (
                    <Row className="yourinfo-v2-row yourinfo-v2-dependent-ages">
                        {dependentAges.map((age, index) => (
                            <Col key={`dependent-age-${index}`} md={6} xs={12}>
                                <Form.Group>
                                    <Form.Label>{`Age of dependent ${index + 1}`}</Form.Label>
                                    <Input
                                        className="yourinfo-v2-prefix-field"
                                        addonBefore="Age"
                                        size="large"
                                        value={age}
                                        onChange={(event) => handleDependentAgeChange(index)(event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        ))}
                    </Row>
                ) : null}

                <Row className="yourinfo-v2-row yourinfo-v2-contact" data-field="phone">
                    <Col>
                        <Form.Group>
                            <Form.Label>*Contact information</Form.Label>
                            <Row className="address-wrap yourinfo-v2-phone-row" style={{ paddingBottom: 0 }}>
                                {phoneTypes.map(renderPhoneInput)}
                            </Row>
                            {fieldErrors.phone ? (
                                <FieldError message={fieldErrors.phone} />
                            ) : null}
                            <BootstrapButton
                                disabled={!isPhoneEmpty(profile.homePhone) && !isPhoneEmpty(profile.mobilePhone) && !isPhoneEmpty(profile.workPhone)}
                                variant="link"
                                className="text-primary p-0 yourinfo-v2-add-phone"
                                style={{
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    color: "#325CEB",
                                    textDecoration: "none",
                                    border: "none",
                                    background: "none",
                                }}
                                onClick={(event) => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    handleAddPhone()
                                }}
                            >
                                Add contact information
                            </BootstrapButton>

                            <div className="text-muted wrap-text yourinfo-v2-spam-note">
                                We hate spam just as much as you do. You won't receive any unwanted calls or texts from us.
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                {showLeadSource && (
                    <Row className="yourinfo-v2-row">
                        <Col md={6} xs={12}>
                            <Form.Group>
                                <Form.Label>Where did you hear about us? (optional)</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={applicationSettings.leadSource || ""}
                                    onChange={(event) => setApplicationSettings((prev) => ({
                                        ...prev,
                                        leadSource: event.target.value || "",
                                    }))}
                                >
                                    <option value="">...</option>
                                    <option value="sunny">From Sunny</option>
                                    <option value="socialmedia">Social Media</option>
                                    <option value="friend">Friend Referral</option>
                                    <option value="website">Website</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>
                )}
            </div>

            <Row className="text-left application-bottom-bar">
                <div className="d-flex align-items-center">
                    <BootstrapButton
                        id="next"
                        type="button"
                        variant="zeitro-primary"
                        disabled={saving}
                        className="text-center operate-btn btn"
                        onClick={handleNext}
                    >
                        Next
                    </BootstrapButton>
                </div>
            </Row>
        </>
    )
}

export default YourInfoV2
